import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Flex, HTMLDialog, Button} from '@priority-software/priority-style-react';
import {DialogToolbarContent} from '../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {FunctionDialogContent} from './parts/Content';
import {showFormClientSideMessage} from '../../../Form/actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';
import {checkAlertExpression} from '../../actions';
import {parseExpressionValue} from './utils';
import {texts} from '../../texts';
import styles from './styles.module.scss';
import {TabsDataItem} from './constants';
import {BPMCheckAlertExpr} from '../../modules/BPMRules/api/BPMCheckAlertExpr';

type Props = {
    onClose: () => void;
    onSave: (value: string) => void;
    onCriticalError: () => void;
    validateExpression?: boolean;
    defaultValue?: string;
    relatedField?: number;
    session: string;
    mainTitle: string;
    helpText?: string;
    optionsData: TabsDataItem[];
    isBPM?: boolean;
    placeHolderText?: string;
    inputDirection?: 'ltr' | 'rtl' | 'auto';
};

const functionDialogId = 'automation-function-dialog';

export const AutomationFunctionComponent = (props: Props) => {
    const {
        onClose,
        placeHolderText,
        mainTitle,
        helpText,
        defaultValue = '',
        relatedField,
        session,
        optionsData,
        validateExpression = true,
        isBPM,
        inputDirection,
        onSave,
        onCriticalError
    } = props;
    const [value, setValue] = useState(parseExpressionValue(defaultValue));
    const inputRef = useRef<HTMLInputElement>(null);
    const exprLastCursorPosition = useRef<number>(value.length);

    const onInputValueChange = useCallback((insertValue: string) => {
        setValue(insertValue);
    }, []);

    const handleChangeByExpressionClick = useCallback((insertValue: string) => {
        const inputValue = inputRef.current.value;
        const position = exprLastCursorPosition.current;

        exprLastCursorPosition.current = exprLastCursorPosition.current + insertValue.length;

        const updatedValue =
            inputValue.slice(0, position) +
            insertValue +
            inputValue.slice(position, inputValue.length);
        setValue(updatedValue);
        inputRef.current.setSelectionRange(updatedValue.length, updatedValue.length);
    }, []);

    const handleSave = useCallback(async () => {
        const expressionValue = value ? `(${parseExpressionValue(value)})` : '';
        if (validateExpression && relatedField) {
            const res = isBPM
                ? await BPMCheckAlertExpr(expressionValue, relatedField.toString())
                : await checkAlertExpression(
                      session,
                      relatedField,
                      expressionValue,
                      onCriticalError
                  );

            if (res.ValidExpr) {
                onSave(expressionValue);
            } else {
                const returnFocus = () => {
                    requestAnimationFrame(() => {
                        inputRef.current?.focus();
                    });
                };
                showFormClientSideMessage(session, priority.netitems.shared.Message.Type.Error, {
                    title: texts.wrong_exp_title,
                    content: texts.wrong_exp_text,
                    onClose: returnFocus
                });
            }
            return;
        }
        onSave(expressionValue);
    }, [value, onSave, session, relatedField, validateExpression]);

    useEffect(() => {
        const inputElement = inputRef.current;

        function handleCursorPosition(e) {
            exprLastCursorPosition.current = e.target.selectionEnd;
        }

        if (inputElement) {
            inputElement.addEventListener('click', handleCursorPosition);
            inputElement.addEventListener('keyup', handleCursorPosition);
        }

        return () => {
            inputElement.removeEventListener('click', handleCursorPosition);
            inputElement.removeEventListener('keyup', handleCursorPosition);
        };
    }, []);

    return (
        <HTMLDialog
            isOpen
            id={functionDialogId}
            width={700}
            size="large"
            componentClasses={'reactComp'}
            onClose={onClose}
            toolbarContent={
                <DialogToolbarContent
                    mainTitle={mainTitle}
                    tooltipPortalID={functionDialogId}
                    helpText={helpText}
                />
            }
            content={
                <FunctionDialogContent
                    direction={inputDirection}
                    optionsData={optionsData}
                    ref={inputRef}
                    value={value}
                    placeHolderText={placeHolderText}
                    onInputValueChange={onInputValueChange}
                    onValueChange={handleChangeByExpressionClick}
                />
            }
            footer={
                <Flex justifyContent="flex-end" componentClasses={styles.footer}>
                    <Button
                        componentClasses={styles.btn}
                        size="large"
                        skin="secondary"
                        onClick={onClose}
                    >
                        {texts.cancel_btn_label}
                    </Button>
                    <Button componentClasses={styles.btn} size="large" onClick={handleSave}>
                        {texts.dlg_saveBtn}
                    </Button>
                </Flex>
            }
        />
    );
};
